import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Comparison,
  ComparisonKey,
  ComparisonNotes,
  Waveform,
  unclippedTail,
} from "./RadarBeatSignal";
import { SettingsDump } from "./RadarCaptureInspector";
import {
  Key,
  LIVE_COLORS,
  SpectrumReadout,
  Stat,
  axisOf,
  markerLabel,
  spectrumDomain,
  ticksFor,
} from "./RadarSpectrum";
import { More, saveJson } from "./radarUi";
import { rtctApi } from "services/rtctApi";
import {
  echoList,
  finite,
  formatMm as mm,
  levelAt,
  levelDomain,
  levelTicks,
  sgtTime as sgt,
  statusText,
} from "utils/radarDiagnostics";
import {
  NEAR_FIELD_BINS,
  WIDTH_DROP_DB,
  adcStats,
  clipFrom,
  clipText,
  compareSpectra,
  finerEcho,
  pickDistance,
  spectrumReachMm,
} from "utils/radarSignal";
import {
  arrivalTally,
  hasSpectrum,
  rawModeSends,
  settingsDumpOf,
  tallyText,
} from "utils/radarStream";
import {
  BASES,
  ECHO_LABELS,
  LABEL_ORDER,
  STATES,
  analysisStatus,
  energyText,
  methodText,
  readAnalysis,
  readEnergy,
  verdict,
} from "utils/radarAnalysis";
import { keepNoteText, nodeAgreement } from "utils/radarEdge";
import {
  BURST_ADC,
  BURST_SPECTRUM,
  burstAccount,
  burstBeatRows,
  burstExport,
  burstFileName,
  burstRows,
  captureVsMean,
  skipText,
} from "utils/radarBurst";

// Rich wakes: one burst in full - the server's analysis of it (our estimate
// beside the radar's reading, dry/wet, the reasons, every echo labelled) and
// the energy it took (firmware .57), its spectra overlaid with their mean and
// the band they span, the radar's pick in each, flicker per range, our quick
// look at the mean, each capture beside the mean, the beat signals, the
// radar's settings dump - and a download of all of it.
export const BURST_COLORS = {
  capture: "#7f97a3",
  band: "#d7e6ec",
  flicker: "#b4532a",
  fft: "#8cc5bc",
};
const LETTERS = "ABCDE";
const db1 = (v) => `${v.toFixed(1)} dB`;
const spread = (v) =>
  v === null || v === undefined ? "—" : `±${v.toFixed(2)} dB`;
const clock = (value) =>
  new Intl.DateTimeFormat("en-SG", {
    timeZone: "Asia/Singapore",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(value));
const mmRange = (lo, hi) =>
  Math.round(lo) === Math.round(hi)
    ? mm(lo, 1)
    : `${Math.round(lo).toLocaleString()}–${mm(hi)}`;

// The tooltip of the overlay: the mean, the band and the flicker at a range.
function BurstTip({ active, payload, label }) {
  const row = active ? payload?.[0]?.payload : null;
  if (!row) return null;
  return (
    <div className="field-burst-tip">
      <b>{Math.round(label).toLocaleString()} mm</b>
      <span>Mean {row.mean === null ? "—" : db1(row.mean)}</span>
      {row.band && (
        <span>
          Captures {row.band[0].toFixed(1)} to {db1(row.band[1])}
        </span>
      )}
      {row.std !== null && row.std !== undefined && (
        <span>Flicker {spread(row.std)}</span>
      )}
    </div>
  );
}

const BurstChart = memo(function BurstChart({
  rows,
  keys,
  domain,
  highlight,
  floor,
  nearArea,
  peaks,
  chosen,
  picks,
  labelled,
  single = false,
}) {
  const yDomain = levelDomain(rows.flatMap((r) => [r.lo, r.hi]));
  return (
    <div className="field-stream-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={rows}
          margin={{ top: 16, right: 14, bottom: 0, left: 0 }}
        >
          <CartesianGrid stroke="#e8eff1" vertical={false} />
          <XAxis
            dataKey="d"
            type="number"
            domain={domain}
            ticks={ticksFor(domain)}
            allowDataOverflow
            tickFormatter={(v) => v.toLocaleString()}
            tick={{ fontSize: 10, fill: "#6d8490" }}
          />
          <YAxis
            domain={yDomain}
            ticks={levelTicks(yDomain)}
            tickFormatter={(v) => Math.round(v)}
            tick={{ fontSize: 10, fill: "#6d8490" }}
            unit=" dB"
            width={62}
          />
          <Tooltip content={<BurstTip />} />
          {nearArea && (
            <ReferenceArea
              x1={nearArea[0]}
              x2={nearArea[1]}
              fill={LIVE_COLORS.near}
              fillOpacity={0.9}
              ifOverflow="hidden"
            />
          )}
          {!single && (
            <Area
              dataKey="band"
              name="Lowest to highest"
              stroke="none"
              fill={BURST_COLORS.band}
              fillOpacity={0.75}
              activeDot={false}
              isAnimationActive={false}
            />
          )}
          {floor !== null && (
            <ReferenceLine
              y={floor}
              stroke={LIVE_COLORS.floor}
              strokeWidth={1.5}
              strokeDasharray="2 3"
            />
          )}
          {(single ? [] : keys).map((key) => {
            const k = Number(key.slice(1));
            const on = k === highlight;
            return (
              <Line
                key={key}
                dataKey={key}
                name={`Capture ${k + 1}`}
                stroke={on ? LIVE_COLORS.echo : BURST_COLORS.capture}
                strokeWidth={on ? 1.6 : 0.8}
                strokeOpacity={on ? 1 : 0.6}
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            );
          })}
          <Line
            dataKey="mean"
            name={single ? "Spectrum" : "Mean"}
            stroke={LIVE_COLORS.spectrum}
            strokeWidth={2.4}
            dot={false}
            isAnimationActive={false}
          />
          {chosen && (
            <ReferenceLine
              segment={[
                { x: chosen.leftMm, y: chosen.level - WIDTH_DROP_DB },
                { x: chosen.rightMm, y: chosen.level - WIDTH_DROP_DB },
              ]}
              stroke={LIVE_COLORS.ours}
              strokeWidth={3}
            />
          )}
          {picks.map((p) => (
            <ReferenceDot
              key={`pick-${p.k}`}
              x={p.mm}
              y={p.level}
              r={3.5}
              fill={LIVE_COLORS.radar}
              stroke="#ffffff"
              strokeWidth={1.5}
            />
          ))}
          {peaks.map((p) => (
            <ReferenceDot
              key={`ours-${p.letter}`}
              x={p.mm}
              y={p.level}
              r={5}
              fill="#ffffff"
              stroke={LIVE_COLORS.ours}
              strokeWidth={2}
              label={markerLabel(p.letter, LIVE_COLORS.ours, true)}
            />
          ))}
          {labelled.map((e) => (
            <ReferenceDot
              key={`label-${e.mm}`}
              className={`field-echo-dot is-${e.label}${e.approx ? " is-approx" : ""}`}
              x={e.mm}
              y={e.level}
              r={5.5}
              // An approximate position (spectrum only) is a hollow, dashed
              // ring, its label marked ≈.
              fill={e.approx ? "#ffffff" : ECHO_LABELS[e.label].color}
              stroke={e.approx ? ECHO_LABELS[e.label].color : "#ffffff"}
              strokeWidth={2}
              strokeDasharray={e.approx ? "3 2" : undefined}
              label={markerLabel(
                `${e.approx ? "≈ " : ""}${ECHO_LABELS[e.label].text.toLowerCase()}`,
                ECHO_LABELS[e.label].color,
                true,
              )}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
});

// The flicker strip: the spread between the captures at each range, on the
// same distance axis as the overlay above it.
const FlickerChart = memo(function FlickerChart({ rows, domain, peak }) {
  const top = Math.max(
    1,
    ...rows.map((r) => (Number.isFinite(r.std) ? r.std : 0)),
  );
  const yMax = Math.ceil(top);
  return (
    <div className="field-burst-flicker">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={rows}
          margin={{ top: 6, right: 14, bottom: 0, left: 0 }}
        >
          <CartesianGrid stroke="#e8eff1" vertical={false} />
          <XAxis
            dataKey="d"
            type="number"
            domain={domain}
            ticks={ticksFor(domain)}
            allowDataOverflow
            tickFormatter={(v) => v.toLocaleString()}
            tick={{ fontSize: 10, fill: "#6d8490" }}
          />
          <YAxis
            domain={[0, yMax]}
            ticks={[0, yMax]}
            tick={{ fontSize: 10, fill: "#6d8490" }}
            unit=" dB"
            width={62}
          />
          <Tooltip
            labelFormatter={(v) => `${Math.round(v).toLocaleString()} mm`}
            formatter={(value) => [spread(finite(value)), "Flicker"]}
          />
          <Area
            dataKey="std"
            name="Flicker"
            type="linear"
            stroke={BURST_COLORS.flicker}
            strokeWidth={1.4}
            fill={BURST_COLORS.flicker}
            fillOpacity={0.2}
            isAnimationActive={false}
          />
          {peak && (
            <ReferenceDot
              x={peak.mm}
              y={peak.std}
              r={4}
              fill={BURST_COLORS.flicker}
              stroke="#ffffff"
              strokeWidth={1.5}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
});

// What a capture holds, in words.
function holds(capture, detail) {
  const list = echoList(detail?.peaks);
  return (
    [
      hasSpectrum(capture) && "spectrum",
      capture.has_thr && "threshold",
      capture.has_adc && "beat signal",
      list?.echoes.length ? `echo list (${list.echoes.length})` : null,
    ]
      .filter(Boolean)
      .join(", ") || "no radar data"
  );
}

// Why the burst has no spectrum, as far as its raw output mode tells; a rich
// reading takes each part it is asked for in its own mode.
function noSpectrumText(mode, rich = false) {
  if (rich)
    return "No spectrum in this rich reading: it holds the beat signal only (below). Tick Spectrum under Rich readings on Node Control to have the radar's own spectrum on every wake too.";
  const sends = rawModeSends(mode);
  if (sends === 0)
    return `No spectrum in this burst: raw output mode ${mode} sends nothing on this radar.`;
  if (sends !== null && !(sends & BURST_SPECTRUM))
    return `No spectrum in this burst: raw output mode ${mode} sends only the beat signal (below). Mode 1 sends the spectrum; the radar sends one kind of packet per mode.`;
  return "No spectrum in this burst's captures. The spectrum comes with Spectrum ticked and a raw output mode that sends it (mode 1).";
}

// The beat signals of the burst's captures that have one, overlaid with the
// clipped part shaded, and our FFT of each (Hann ×8) with their mean and our
// rectangular mean beside the radar's mean spectrum.
function BurstBeats({
  captures,
  details,
  beats,
  beat,
  meanDb,
  step,
  cal,
  domain,
  mode,
  unclipped,
  onUnclipped,
  spectra = 0,
}) {
  const withAdc = captures.filter((c) => c.has_adc);
  const loaded = withAdc.map((c) => details.get(String(c.id)));
  if (!withAdc.length) {
    const sends = rawModeSends(mode);
    return (
      <div className="field-radar-empty">
        No beat signal in this burst.{" "}
        {sends !== null && !(sends & BURST_ADC)
          ? `In raw output mode ${mode} the radar sends no beat signal; mode 2 sends it, without the spectrum: the radar sends one kind of packet per mode (or alternate the two).`
          : "It needs Beat signal ticked and a raw output mode that sends it (mode 2 on node 1's radar, without the spectrum: the radar sends one kind of packet per mode), or alternation."}
      </div>
    );
  }
  const errors = loaded.filter((d) => d?.error).length;
  if (!beats.length)
    return errors ? (
      <div className="field-alert" role="alert">
        The beat signals could not load: {loaded.find((d) => d?.error).error}
      </div>
    ) : (
      <p className="field-caption">Loading the beat signals…</p>
    );
  const series = beats.map((b) => b.samples);
  const one = beat.ffts.length <= 1;
  const stats = series.map((s) => adcStats(s));
  const rails = stats.reduce((sum, s) => sum + (s?.clipped || 0), 0);
  const clips = beats.map((b) => b.clip).filter((c) => c?.clipped);
  const shade = clips.length
    ? [
        Math.min(...clips.map((c) => c.first)),
        Math.max(...clips.map((c) => c.last)),
      ]
    : null;
  const tails = beats.map((b) => unclippedTail(b.clip, b.samples.length));
  const canTail = clips.length > 0 && tails.some((t) => t !== null);
  const picks = beat.ffts
    .map((f) => compareSpectra(meanDb, f).ours?.bin)
    .filter(Number.isFinite)
    .map((bin) => bin * step - cal);
  const radar = meanDb ? compareSpectra(meanDb, null).radar : null;
  return (
    <div className="field-stream-beat">
      <div>
        <h4 className="field-stream-subhead">
          {series.length === 1
            ? "The beat signal"
            : `${series.length} beat signals, overlaid`}
        </h4>
        {beats.map((b) =>
          b.clip?.clipped ? (
            <p
              className="field-radar-pill is-warning"
              role="note"
              key={b.number}
            >
              {`Capture ${b.number} ${clipText(b.clip)}`}
            </p>
          ) : null,
        )}
        <Waveform
          series={series}
          names={beats.map((b) => `Capture ${b.number}`)}
          shade={shade}
        />
        <p className="field-caption">
          {Math.min(...stats.map((s) => s.min)).toLocaleString()} to{" "}
          {Math.max(...stats.map((s) => s.max)).toLocaleString()} counts
          {series.length === 1 ? "" : " across the burst"} ·{" "}
          {rails ? `${rails} at the int16 limits` : "none at the int16 limits"}
          {series.length < withAdc.length
            ? ` · ${withAdc.length - series.length} still loading or missing`
            : ""}
          .
          {clips.length
            ? ` The receiver clips in ${clips.length} of ${series.length}, shaded over samples ${shade[0]}-${shade[1]}.`
            : ""}
        </p>
        {clips.length > 0 && (
          <More label="What clipping means" className="is-why">
            <p className="field-caption">
              Analog saturation on a strong close echo, not the int16 limits.
              Clipping adds harmonics at whole multiples of a strong echo's
              distance: echoes at 2-4 times the strongest are marked as possible
              harmonics in our echo table.
            </p>
          </More>
        )}
        {clips.length > 0 && (
          <label className="field-burst-toggle">
            <input
              type="checkbox"
              checked={unclipped && canTail}
              disabled={!canTail}
              onChange={(e) => onUnclipped(e.target.checked)}
            />
            Unclipped part only
            <small>
              {canTail
                ? "Our Hann FFT of each over the samples after its last clipped one: lower resolution."
                : "Too few samples after the clipping for an FFT."}
            </small>
          </label>
        )}
      </div>
      <div>
        <h4 className="field-stream-subhead">
          {one
            ? meanDb
              ? `Our FFT beside the radar's ${spectra === 1 ? "" : "mean "}spectrum`
              : "Our FFT (no spectrum with it to compare)"
            : meanDb
              ? "Our FFT of each beside the radar's mean spectrum"
              : "Our FFT of each (the burst has no spectrum to compare)"}
        </h4>
        <Comparison
          rows={beat.rows}
          domain={domain}
          hasRadar={!!meanDb}
          thin={one ? 0 : beat.ffts.length}
        />
        <ComparisonKey
          hasRadar={!!meanDb}
          thin={!one}
          tail={
            unclipped && canTail ? "samples after each one's clipping" : null
          }
        />
        <p className="field-caption">
          {one ? "" : "Thin lines: each capture's FFT; bold: their mean. "}
          {picks.length
            ? `Strongest echo beyond the near field in our FFT${one ? "" : "s"}: ${mmRange(Math.min(...picks), Math.max(...picks))}${radar ? `; in the radar's ${spectra === 1 ? "" : "mean "}spectrum ${mm(radar.bin * step - cal, 1)}` : ""}.`
            : `Our FFT${one ? "" : "s"} find${one ? "s" : ""} no echo beyond the near field.`}
        </p>
        <ComparisonNotes />
      </div>
    </div>
  );
}

// The server's analysis of one burst: its sentence (why), our estimate
// beside the radar's reading, dry/wet/unknown with its confidence and a
// reason for every point lost, every echo with what it is (≈ where its
// position is approximate), how it was reached; and the energy the burst
// took (firmware .57). `status`: "ready", "pending" (the server has not
// analysed it yet) or "none" (a server before analyses). From firmware .62
// the node's own estimator-v2 estimate of the beat signals beside ours: the
// same echo within a rounding, else the one our empty-pipe map, floor rule or
// tracker chose instead.
function BurstAnalysis({ analysis, status, energy, raw }) {
  const said = verdict(analysis);
  const how = methodText(analysis);
  const approx = analysis?.echoes.some((e) => e.approx);
  const node = analysis
    ? nodeAgreement(analysis.edge2Mm, analysis.edge2DiffMm, analysis.edge2N)
    : null;
  return (
    <div
      className="field-burst-analysis"
      role="group"
      aria-labelledby="field-burst-analysis"
    >
      <h4 id="field-burst-analysis" className="field-stream-subhead">
        Our analysis of this burst
      </h4>
      {analysis ? (
        <>
          {analysis.why && (
            <p
              className={`field-analysis-why is-${analysis.state}`}
              aria-label="Our analysis, in a sentence"
            >
              {analysis.why}
            </p>
          )}
          <div className="field-stream-readout">
            <Stat
              label="Radar's own reading"
              value={
                analysis.radarMm !== null ? mm(analysis.radarMm) : "No reading"
              }
              detail="Its own pick, as reported"
            />
            <Stat
              label="Our estimate"
              value={
                analysis.ourMm !== null ? mm(analysis.ourMm) : "No estimate"
              }
              detail={`${STATES[analysis.state].label}${analysis.confidence !== null ? ` · confidence ${analysis.confidence} %` : ""}`}
            />
            {(analysis.floorMm !== null ||
              analysis.expectedFloorMm !== null) && (
              <Stat
                label="Floor"
                value={
                  analysis.floorMm !== null ? mm(analysis.floorMm) : "Not seen"
                }
                // Without an expected floor, version 2 takes the strongest
                // echo in the pipe's last 15 % (version 1: the farthest strong
                // echo); its sentence above says which.
                detail={
                  analysis.expectedFloorMm !== null
                    ? `Expected at ${mm(analysis.expectedFloorMm)}`
                    : Number(analysis.version) >= 2
                      ? "From the pipe's length: commission the node"
                      : "The farthest strong echo in the pipe"
                }
              />
            )}
            {(analysis.clipPct !== null ||
              analysis.flickerDb !== null ||
              analysis.noiseDb !== null) && (
              <Stat
                label="Clipping · flicker"
                value={`${analysis.clipPct !== null ? `${analysis.clipPct.toFixed(1)} %` : "—"} · ${analysis.flickerDb !== null ? `${analysis.flickerDb.toFixed(1)} dB` : "—"}`}
                detail={
                  analysis.noiseDb !== null
                    ? `Noise floor ${db1(analysis.noiseDb)}`
                    : "As the server measured them"
                }
              />
            )}
            {node && (
              <Stat
                label="On-node estimate"
                value={
                  analysis.edge2Mm !== null ? mm(analysis.edge2Mm, 1) : "—"
                }
                detail={
                  node.tone === "agree"
                    ? "The same echo as ours"
                    : node.tone === "disagree"
                      ? `${mm(Math.abs(analysis.edge2DiffMm), 1)} from ours`
                      : "Estimator v2, on the node"
                }
              />
            )}
          </div>
          {said && (
            <p className={`field-analysis-verdict is-${said.tone}`}>
              {said.text}
            </p>
          )}
          {node && (
            <p className={`field-analysis-verdict is-${node.tone}`}>
              {node.text}
            </p>
          )}
          {analysis.reasons.length > 0 && (
            <ul
              className="field-analysis-reasons"
              aria-label="Why, for this burst"
            >
              {analysis.reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          )}
          {(analysis.echoes.length > 0 || how || approx) && (
            <More label="Echoes and method">
              {analysis.echoes.length > 0 && (
                <div className="field-stream-table">
                  <table
                    className="field-table"
                    aria-label="Echoes, labelled by our analysis"
                  >
                    <thead>
                      <tr>
                        <th scope="col">Distance</th>
                        <th scope="col">Level</th>
                        <th scope="col">SNR</th>
                        <th scope="col">What it is</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysis.echoes.map((e) => (
                        <tr key={e.mm} className={e.approx ? "is-approx" : ""}>
                          <td>
                            {e.approx && (
                              <abbr title="Approximate: from the spectrum alone">
                                ≈{" "}
                              </abbr>
                            )}
                            {mm(e.mm, 1)}
                          </td>
                          <td>{e.db === null ? "—" : db1(e.db)}</td>
                          <td>{e.snr === null ? "—" : db1(e.snr)}</td>
                          <td>
                            <span
                              className="field-echo-label"
                              style={{ "--key": ECHO_LABELS[e.label].color }}
                            >
                              {ECHO_LABELS[e.label].text}
                            </span>{" "}
                            <small>
                              {e.label === "clutter" && e.basis
                                ? `a fixed echo, ${BASES[e.basis]}`
                                : ECHO_LABELS[e.label].meaning}
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {(how || approx) && (
                <p className="field-caption" style={{ margin: 0 }}>
                  {how && `${how}.`}
                  {approx &&
                    `${how ? " " : ""}≈ marks a position from the spectrum alone: on node 1 it can sit about a range cell (≈31.5 mm) long; bursts that alternate the spectrum with the beat signal give the precise one.`}
                </p>
              )}
            </More>
          )}
        </>
      ) : (
        <p className="field-caption" style={{ margin: 0 }}>
          {status === "pending"
            ? "Analysis pending for this burst: the server analyses a few bursts each time it is asked, the newest first, and this view asks again every few seconds."
            : "Analysis not available yet for this burst: the server has not analysed it (that needs the updated backend)."}{" "}
          Our quick look at its mean spectrum is below.
        </p>
      )}
      <p className="field-burst-energy">
        <b>Energy</b>{" "}
        {energy
          ? energyText(raw)
          : "not measured: firmware rtct.57 measures each burst's charge, current and solar current."}
      </p>
    </div>
  );
}

export default function RadarBurstDetail({
  mac,
  summary,
  burst,
  details,
  unit = null,
  range = "near",
  // The node's raw-upload rules (readEdge()), for its keep note.
  keepRules = {},
}) {
  const [highlight, setHighlight] = useState(null);
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [unclipped, setUnclipped] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const captures = summary.captures;
  // The mean as a capture, else the burst's axis (burstStep(): a beat signal
  // alone carries no step of its own).
  const ref = useMemo(
    () =>
      summary.mean || {
        db: new Array(256).fill(null),
        step_mm: summary.step ?? null,
        cal_mm: captures[0]?.cal_mm ?? null,
      },
    [summary.mean, summary.step, captures],
  );
  const axis = axisOf(ref);
  const domain = useMemo(() => spectrumDomain(ref, range), [ref, range]);
  const { rows, keys } = useMemo(
    () => burstRows(summary, domain),
    [summary, domain],
  );
  const inView = (x) => x !== null && x >= domain[0] && x <= domain[1];
  const analysis = summary.analysis;
  const reach = spectrumReachMm(axis.step, axis.cal, ref.db.length);
  const picks = captures
    .map((c, k) => {
      const p = pickDistance(c.air_mm, reach);
      if (!inView(p)) return null;
      const points = rows
        .filter((r) => r[`c${k}`] !== null && r[`c${k}`] !== undefined)
        .map((r) => ({ r: r.d, db: r[`c${k}`] }));
      const level = levelAt(points, p);
      return level === null ? null : { k, mm: p, level };
    })
    .filter(Boolean);
  // The server's analysis of the burst: its echoes, labelled, take the
  // place of our quick look's rings on the chart.
  const served = useMemo(() => readAnalysis(burst.analysis), [burst.analysis]);
  const energy = readEnergy(burst.energy);
  const meanPoints = useMemo(
    () =>
      rows
        .filter((r) => r.mean !== null && r.mean !== undefined)
        .map((r) => ({ r: r.d, db: r.mean })),
    [rows],
  );
  const labelled = (served?.echoes || [])
    .filter((e) => inView(e.mm))
    .map((e) => ({ ...e, level: levelAt(meanPoints, e.mm) }))
    .filter((e) => e.level !== null);
  const labelsShown = LABEL_ORDER.filter((l) =>
    labelled.some((e) => e.label === l),
  );
  const peaks = labelled.length
    ? []
    : (analysis?.peaks || [])
        .map((p, i) => ({ ...p, letter: LETTERS[i] }))
        .filter((p) => inView(p.mm));
  const chosen = analysis?.chosen;
  const width =
    chosen &&
    chosen.leftMm !== null &&
    inView(chosen.leftMm) &&
    inView(chosen.rightMm)
      ? chosen
      : null;
  const nearEnd = (NEAR_FIELD_BINS - 0.5) * axis.step - axis.cal;
  const nearArea =
    nearEnd > domain[0]
      ? [Math.max(domain[0], -0.5 * axis.step - axis.cal), nearEnd]
      : null;
  // The beat signals of the burst: their FFTs (all samples: the finer
  // position for the readout, and clipping), and as shown (the unclipped
  // part only, when asked).
  const beats = useMemo(
    () =>
      captures
        .filter((c) => c.has_adc)
        .map((c) => {
          const data = details.get(String(c.id))?.data;
          const samples = Array.isArray(data?.adc)
            ? data.adc.map(Number).filter(Number.isFinite)
            : [];
          // The server's clipping (clip) when it has one, else ours; the
          // capture's own number, as in the table of captures.
          return samples.length >= 8
            ? {
                samples,
                clip: clipFrom(data, samples),
                number: (finite(c.i) ?? captures.indexOf(c)) + 1,
              }
            : null;
        })
        .filter(Boolean),
    [captures, details],
  );
  const meanDb = summary.mean ? summary.stats.mean : null;
  const fullBeat = useMemo(
    () => burstBeatRows(beats, meanDb, axis.step, axis.cal, domain),
    [beats, meanDb, axis.step, axis.cal, domain],
  );
  const shownBeat = useMemo(
    () =>
      unclipped
        ? burstBeatRows(beats, meanDb, axis.step, axis.cal, domain, {
            unclipped: true,
          })
        : fullBeat,
    [unclipped, fullBeat, beats, meanDb, axis.step, axis.cal, domain],
  );
  const finer = useMemo(() => {
    const near = analysis?.chosen?.bin ?? analysis?.pick?.bin;
    if (!fullBeat.mean || near === null || near === undefined) return null;
    const found = finerEcho(fullBeat.mean, near);
    return found
      ? {
          bin: found.bin,
          mm: found.bin * axis.step - axis.cal,
          source: `of this burst (${fullBeat.ffts.length} capture${fullBeat.ffts.length === 1 ? "" : "s"})`,
        }
      : null;
  }, [fullBeat, analysis, axis.step, axis.cal]);
  const clip = fullBeat.clips.find((c) => c?.clipped) || null;
  const vsMean = useMemo(
    () => captures.map((c) => captureVsMean(c, summary.stats)),
    [captures, summary.stats],
  );
  const flicker = summary.flicker;
  const peakInView =
    flicker.peak && inView(flicker.peak.mm) ? flicker.peak : null;
  const dump = useMemo(() => {
    for (const c of captures) {
      const found = settingsDumpOf(details.get(String(c.id))?.data);
      if (found) return found;
    }
    return null;
  }, [captures, details]);
  // Each capture's own raw output mode once its record is in (a rich reading
  // takes its beat signal in mode 2 and its spectrum in mode 1; the list
  // gives the burst one), else the burst's.
  const tally = useMemo(
    () =>
      arrivalTally(
        captures.map((c) => ({
          ...c,
          raw_mode:
            c.raw_mode ??
            details.get(String(c.id))?.data?.raw_mode ??
            burst.raw_mode,
        })),
      ),
    [captures, details, burst.raw_mode],
  );
  const account = burstAccount(burst.reading?.jjy_burst);
  const reading = burst.reading;
  // Firmware .62's raw uploads: why the node sent this wake's raw captures.
  const keepNote = keepNoteText(reading?.keep, keepRules);
  const spanS =
    summary.lastMs !== null && summary.ms !== null
      ? (summary.lastMs - summary.ms) / 1000
      : null;
  const picked = summary.picks;
  // A rich reading (firmware .58: one small burst a wake) or a classic burst.
  const rich = summary.kind === "rich";
  // Flicker, the band and each capture against the mean need two spectra.
  const single = summary.stats.n < 2;
  const holdsText = [
    captures.some((c) => c.has_adc) && "beat signal",
    captures.some(hasSpectrum) && "spectrum",
  ]
    .filter(Boolean)
    .join(" and ");

  const download = async () => {
    setBusy(true);
    const all = new Map(details);
    for (const c of burst.captures || []) {
      const key = String(c?.id);
      if (c?.id === null || c?.id === undefined || all.get(key)?.data) continue;
      try {
        all.set(key, { data: await rtctApi.radarCapture(c.id), error: "" });
      } catch (e) {
        all.set(key, { data: null, error: e.message });
      }
    }
    saveJson(
      burstFileName(mac, burst),
      burstExport({
        mac,
        burst,
        summary,
        details: all,
        exportedAt: new Date().toISOString(),
      }),
    );
    if (alive.current) setBusy(false);
  };

  return (
    <div className="field-burst-detail">
      <div className="field-stream-inspector-head">
        <div>
          <p className="field-caption" style={{ margin: 0 }}>
            {[
              rich ? "Rich reading" : null,
              sgt(burst.t_first),
              `${summary.received.toLocaleString()} of ${summary.expected ?? "?"} captures${spanS !== null && spanS > 0 ? ` over ${spanS.toFixed(1)} s` : ""}`,
              rich
                ? holdsText || "no radar data"
                : summary.mode !== null
                  ? `raw output mode ${summary.mode}`
                  : "raw output mode not reported",
              reading
                ? `the wake's reading ${finite(reading.distance_mm) !== null ? mm(reading.distance_mm, 1) : "no distance"}${reading.jjy_status ? ` (${statusText(reading.jjy_status)})` : ""}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
          {account && (
            <p className="field-stream-arrived">
              The node took {account.ok ?? "?"} of {account.planned ?? "?"}
              {account.fail ? `, ${account.fail} failed` : ""}
              {account.ms !== null
                ? ` in ${(account.ms / 1000).toFixed(1)} s`
                : ""}
              {account.skip ? `; ${skipText(account.skip)}` : ""}.
            </p>
          )}
          {tally.length > 0 && (
            <More label="What arrived">
              <p className="field-stream-arrived">
                What arrived: {tally.map(tallyText).join("; ")}.
              </p>
            </More>
          )}
          {rich && keepNote && (
            <p className="field-stream-arrived">{keepNote}</p>
          )}
        </div>
        <button
          type="button"
          className="field-button"
          disabled={busy}
          onClick={download}
        >
          {busy ? "Preparing the file…" : "Download burst (JSON)"}
        </button>
      </div>
      <BurstAnalysis
        analysis={served}
        status={analysisStatus(burst)}
        energy={energy}
        raw={burst.energy}
      />
      {summary.mean ? (
        <div className="field-stream-spectrum">
          <p className="field-radar-axis">
            {unit ||
              "dB = 20·log10(bin value), assuming linear magnitude (unconfirmed)"}
          </p>
          <BurstChart
            rows={rows}
            keys={keys}
            domain={domain}
            highlight={highlight}
            floor={analysis?.floor ?? null}
            nearArea={nearArea}
            peaks={peaks}
            chosen={width}
            picks={picks}
            labelled={labelled}
            single={single}
          />
          <ul className="field-radar-legend" aria-label="Burst spectrum key">
            <Key color={LIVE_COLORS.spectrum}>
              {single
                ? "The spectrum (one capture)"
                : `Mean of ${summary.stats.n} spectra`}
            </Key>
            {!single && <Key color={BURST_COLORS.capture}>Each capture</Key>}
            {!single && (
              <Key kind="area" color={BURST_COLORS.band}>
                Lowest to highest
              </Key>
            )}
            {picks.length > 0 && (
              <Key kind="dot" color={LIVE_COLORS.radar}>
                {single ? "Radar's pick" : "Radar's pick in each capture"}
              </Key>
            )}
            {analysis?.floor !== null && analysis?.floor !== undefined && (
              <Key kind="dotted" color={LIVE_COLORS.floor}>
                Noise floor{single ? "" : " of the mean"} (ours){" "}
                {db1(analysis.floor)}
              </Key>
            )}
            {peaks.length > 0 && (
              <Key kind="ring" color={LIVE_COLORS.ours}>
                Our echoes on the mean {peaks.map((p) => p.letter).join(" ")}
              </Key>
            )}
            {labelsShown.map((l) => (
              <Key key={l} kind="dot" color={ECHO_LABELS[l].color}>
                {ECHO_LABELS[l].text}: {ECHO_LABELS[l].meaning}
              </Key>
            ))}
            {labelled.some((e) => e.approx) && (
              <Key kind="ring" color="#5f7682">
                ≈ hollow, dashed: an approximate position (spectrum only)
              </Key>
            )}
            {width && (
              <Key kind="bar" color={LIVE_COLORS.ours}>
                Chosen echo's −{WIDTH_DROP_DB} dB width
              </Key>
            )}
            {nearArea && (
              <Key kind="area" color={LIVE_COLORS.near}>
                Near field (bins 0–{NEAR_FIELD_BINS - 1})
              </Key>
            )}
          </ul>
          <div
            className="field-burst-flicker-block"
            role="group"
            aria-labelledby="field-burst-flicker-title"
          >
            <h4 id="field-burst-flicker-title" className="field-stream-subhead">
              Flicker: how much the captures differ at each range
            </h4>
            {single ? (
              <More
                label={`n/a with one ${captures.length === 1 ? "capture" : "spectrum"}`}
                className="is-why"
              >
                <p className="field-caption field-burst-na">
                  {captures.length === 1
                    ? "Flicker compares two spectra or more of one burst."
                    : "The other captures hold beat signals, and flicker compares two spectra or more of one burst."}{" "}
                  {rich
                    ? "A rich reading takes one of each part a wake; the history below follows it from wake to wake."
                    : "A burst of several spectra gives it."}
                </p>
              </More>
            ) : (
              <>
                <FlickerChart rows={rows} domain={domain} peak={peakInView} />
                <p className="field-caption">
                  {`Standard deviation between the ${summary.stats.n} spectra, per bin: median ${spread(flicker.median)} beyond the near field${flicker.peak ? `, strongest ${spread(flicker.peak.std)} at ${mm(flicker.peak.mm)}` : ""}${flicker.echo !== null ? `, ${spread(flicker.echo)} at the chosen echo` : ""}. Steady on node 1 is about 0.2 dB, at most 1-2 dB; insects, drops or ripple show as a high spread at their range.`}
                </p>
              </>
            )}
          </div>
          <More
            className="field-burst-quicklook"
            label={`Our quick look at the ${single ? "" : "mean "}spectrum, in this browser`}
          >
            <SpectrumReadout
              capture={summary.mean}
              analysis={analysis}
              finer={finer}
              clip={clip}
              pick={{
                label: "Radar's picks",
                value: picked.count
                  ? mmRange(picked.min, picked.max)
                  : "No valid pick",
                detail: picked.count
                  ? `Median ${mm(picked.median, 1)} · ${picked.count} of ${captures.length} captures`
                  : "None of the captures gave a valid distance",
              }}
            />
            <p className="field-radar-axis is-x">
              Distance in mm as the node reports it: bin × {mm(axis.step, 2)}
              {axis.assumedStep ? " (assumed)" : ""} − {mm(axis.cal, 1)}{" "}
              calibration offset.{" "}
              {single
                ? "Our analysis runs on the capture's spectrum."
                : "The mean is of the dB values (bins without a value left out); our analysis runs on it like on any capture."}
            </p>
          </More>
        </div>
      ) : (
        <div className="field-radar-empty">
          {noSpectrumText(summary.mode, rich)}
        </div>
      )}
      <More
        label={`Each capture (${captures.length.toLocaleString()})`}
        className="field-burst-captures"
      >
        <div className="field-stream-table">
          <table className="field-table" aria-label="Captures in this burst">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Time</th>
                <th scope="col">Radar's pick</th>
                <th scope="col">Chosen echo (ours)</th>
                <th scope="col">SNR</th>
                <th scope="col">Near field</th>
                <th scope="col">Off the mean</th>
                <th scope="col">Holds</th>
              </tr>
            </thead>
            <tbody>
              {captures.map((c, k) => {
                const own = vsMean[k];
                const pick = pickDistance(
                  c.air_mm,
                  spectrumReachMm(c.step_mm, c.cal_mm, c.db?.length || 256),
                );
                const number = (finite(c.i) ?? k) + 1;
                return (
                  <tr
                    key={c.id ?? k}
                    className={highlight === k ? "is-highlighted" : undefined}
                  >
                    <td>
                      <button
                        type="button"
                        className="field-burst-pin"
                        aria-pressed={highlight === k}
                        aria-label={`Highlight capture ${number}`}
                        disabled={single || !hasSpectrum(c)}
                        onClick={() =>
                          setHighlight((old) => (old === k ? null : k))
                        }
                      >
                        {number}
                      </button>
                    </td>
                    <td>{c.t ? clock(c.t) : "—"}</td>
                    <td>{pick === null ? "No valid pick" : mm(pick, 1)}</td>
                    <td>
                      {own.analysis?.chosen
                        ? mm(own.analysis.chosen.mm, 1)
                        : "—"}
                    </td>
                    <td>
                      {own.analysis?.chosen?.snr !== null &&
                      own.analysis?.chosen?.snr !== undefined
                        ? db1(own.analysis.chosen.snr)
                        : "—"}
                    </td>
                    <td>
                      {own.analysis?.nearField?.overFloor !== null &&
                      own.analysis?.nearField?.overFloor !== undefined
                        ? `+${own.analysis.nearField.overFloor.toFixed(1)} dB`
                        : "—"}
                    </td>
                    <td>{single ? "n/a" : spread(own.offMean)}</td>
                    <td>{holds(c, details.get(String(c.id))?.data)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="field-caption">
            {single
              ? "Each capture analysed on its own; off the mean needs two spectra or more."
              : "Each capture analysed on its own; off the mean is the RMS difference from the burst's mean beyond the near field, so the capture that flickers stands out. A number highlights its curve above."}
          </p>
        </div>
      </More>
      <div
        className="field-burst-part"
        role="group"
        aria-labelledby="field-burst-beat"
      >
        <h4 id="field-burst-beat" className="field-stream-subhead">
          Beat signals
        </h4>
        <BurstBeats
          captures={captures}
          details={details}
          beats={beats}
          beat={shownBeat}
          meanDb={meanDb}
          step={axis.step}
          cal={axis.cal}
          domain={domain}
          mode={summary.mode}
          unclipped={unclipped}
          onUnclipped={setUnclipped}
          spectra={summary.stats.n}
        />
      </div>
      <div
        className="field-burst-part"
        role="group"
        aria-labelledby="field-burst-dump"
      >
        <h4 id="field-burst-dump" className="field-stream-subhead">
          Settings dump
        </h4>
        <More label={dump ? "Show the dump" : "None with this burst"}>
          {dump ? (
            <SettingsDump dump={dump} reveal={reveal} onReveal={setReveal} />
          ) : captures.some((c) => !details.has(String(c.id))) ? (
            <p className="field-caption">Loading the captures' details…</p>
          ) : (
            <p className="field-caption">
              No settings dump with this burst. Tick Settings dump in Radar rich
              data on Node Control: the node sends one, once, with its next
              burst.
            </p>
          )}
        </More>
      </div>
    </div>
  );
}
